import {
  useRef,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';

import { useScrollEdges } from '../hooks/useScrollEdges';
import { classFor } from '../lib/class-map';
import { cn } from '../lib/cn';

import s from './Table.module.css';

export type TableCollapse = 'card' | 'scroll';
type Variant = 'framed' | 'plain';
type Align = 'start' | 'center' | 'end';

export interface TableProps extends Omit<TableHTMLAttributes<HTMLTableElement>, 'className'> {
  /** Visually hidden `<caption>`; also names the scroll region. Required. */
  caption: string;
  /**
   * Presentation below 640px. 'card': each row becomes a stacked card (lists
   * a phone reads one item at a time). 'scroll': the grid stays and the
   * wrapper scrolls sideways with edge fades (numeric ledgers, previews).
   */
  collapse?: TableCollapse;
  /** 'framed' = bordered elevated surface (list pages). 'plain' = no chrome. */
  variant?: Variant;
  /** Floor for the grid width in scroll mode, e.g. '44rem'. */
  minWidth?: string;
  stickyHeader?: boolean;
  /** Applied to the outer frame, not the `<table>`. */
  className?: string;
  children: ReactNode;
}

const COLLAPSE_CLASS: Record<TableCollapse, string> = {
  card: s.card ?? '',
  scroll: s.scroll ?? '',
};
const VARIANT_CLASS: Record<Variant, string> = {
  framed: s.framed ?? '',
  plain: s.plain ?? '',
};
const ALIGN_CLASS: Record<Align, string> = {
  start: '',
  center: s.alignCenter ?? '',
  end: s.alignEnd ?? '',
};

/**
 * The one responsive table.
 *
 * Stays a real `<table>` at every width — screen readers keep row and column
 * semantics, and `thead` is clipped rather than hidden in card mode so column
 * headers survive in the accessibility tree. Each `Td` carries the column name
 * in `data-label`, which the card layout prints before the value.
 */
export function Table({
  caption,
  collapse = 'card',
  variant = 'framed',
  minWidth,
  stickyHeader = false,
  className,
  children,
  ...rest
}: TableProps): JSX.Element {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const edges = useScrollEdges(scrollerRef);
  const style =
    minWidth === undefined ? undefined : ({ '--table-min-w': minWidth } as CSSProperties);
  const isScroll = collapse === 'scroll';

  return (
    <div
      className={cn(
        s.frame,
        classFor(VARIANT_CLASS, variant),
        classFor(COLLAPSE_CLASS, collapse),
        stickyHeader ? s.sticky : undefined,
        className,
      )}
      data-fade-start={edges.start ? '' : undefined}
      data-fade-end={edges.end ? '' : undefined}
    >
      <div
        ref={scrollerRef}
        className={s.scroller}
        role={isScroll ? 'region' : undefined}
        aria-label={isScroll ? caption : undefined}
        // Focusable only when it actually scrolls (WCAG 2.1.1): a keyboard user
        // needs a way to move the content, and never a useless tab stop.
        tabIndex={isScroll && edges.overflowing ? 0 : undefined}
      >
        <table className={s.table} style={style} {...rest}>
          <caption className={s.srOnly}>{caption}</caption>
          {children}
        </table>
      </div>
    </div>
  );
}

export function Thead(props: HTMLAttributes<HTMLTableSectionElement>): JSX.Element {
  return <thead {...props} />;
}

export function Tbody(props: HTMLAttributes<HTMLTableSectionElement>): JSX.Element {
  return <tbody {...props} />;
}

export function Tr(props: HTMLAttributes<HTMLTableRowElement>): JSX.Element {
  return <tr {...props} />;
}

export interface ThProps extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'scope' | 'align'> {
  /** 'col' (default). 'row' inside Tbody makes the cell the card's title. */
  scope?: 'col' | 'row';
  numeric?: boolean;
  align?: Align;
  /** Hide the header text visually (action columns) — still a columnheader. */
  srOnly?: boolean;
}

export function Th({
  scope = 'col',
  numeric = false,
  align = 'start',
  srOnly = false,
  className,
  children,
  ...rest
}: ThProps): JSX.Element {
  return (
    <th
      scope={scope}
      className={cn(
        scope === 'row' ? s.rowHead : s.th,
        numeric ? s.numeric : undefined,
        classFor(ALIGN_CLASS, align),
        className,
      )}
      {...rest}
    >
      {srOnly ? <span className={s.srOnly}>{children}</span> : children}
    </th>
  );
}

export interface TdProps extends Omit<TdHTMLAttributes<HTMLTableCellElement>, 'align'> {
  /** Column name printed before the value in card mode. Omit on primary/actions cells. */
  label?: string;
  /** The card's title: first, larger, no label. One per row. */
  primary?: boolean;
  /** The card's footer: end-aligned, full width, 44px targets. One per row. */
  actions?: boolean;
  numeric?: boolean;
  align?: Align;
  /** Drop this cell in card mode (secondary columns only). */
  hideOnCard?: boolean;
}

export function Td({
  label,
  primary = false,
  actions = false,
  numeric = false,
  align = 'start',
  hideOnCard = false,
  className,
  children,
  ...rest
}: TdProps): JSX.Element {
  return (
    <td
      className={cn(s.td, numeric ? s.numeric : undefined, classFor(ALIGN_CLASS, align), className)}
      data-label={label}
      data-primary={primary ? '' : undefined}
      data-actions={actions ? '' : undefined}
      data-hide-card={hideOnCard ? '' : undefined}
      {...rest}
    >
      {children}
    </td>
  );
}
